import { type FormEvent, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { authApi } from '../api/auth.js';
import { ApiError } from '../api/client.js';
import { Logo } from '../components/Logo.js';
import { useAuth } from '../auth/AuthContext.js';
import { deriveAndStoreKey, readTenantFromJwt } from '../auth/crypto-key.js';

export function LoginPage(): JSX.Element {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleSubmit(e: FormEvent): Promise<void> {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      const res = await authApi.login(email.trim(), password);
      setSession({
        token: res.token,
        kind: 'temp',
        expiresAt: res.expires_at,
        mfaStatus: res.mfa_status,
      });
      // Derivamos la key AES para IndexedDB AQUI — es el unico momento
      // en que tenemos el password. Sprint 4 / ADR-002.
      const tid = readTenantFromJwt(res.token);
      if (tid) {
        try {
          await deriveAndStoreKey(password, tid);
        } catch {
          // Si la derivacion falla (Web Crypto no disponible) el modo
          // offline simplemente no funcionara; el login normal sigue.
        }
      }
      navigate(res.mfa_status === 'configured' ? '/mfa/verify' : '/mfa/setup', { replace: true });
    } catch (err) {
      if (err instanceof ApiError && err.status === 423) {
        const retry =
          typeof err.details === 'object' &&
          err.details !== null &&
          'retry_after_seconds' in err.details
            ? Number(err.details.retry_after_seconds)
            : 0;
        const mins = Math.max(1, Math.ceil(retry / 60));
        setError(`Cuenta bloqueada temporalmente. Intenta de nuevo en ${mins} min.`);
      } else if (err instanceof ApiError && err.status === 429) {
        setError('Demasiados intentos. Espera unos minutos.');
      } else {
        setError('Credenciales invalidas.');
      }
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <main className="flex min-h-full items-center justify-center px-6 py-12">
      <form
        onSubmit={(e) => {
          void handleSubmit(e);
        }}
        className="w-full max-w-sm space-y-5"
        noValidate
      >
        <div className="text-center space-y-2">
          <Logo iconHeight={40} className="mx-auto" ariaLabel="" />
          <h1 className="sr-only">Cumple</h1>
          <p className="text-sm text-slate-500">Inicia sesión para continuar</p>
        </div>

        <div className="space-y-2">
          <label htmlFor="email" className="block text-sm font-medium text-slate-700">
            Email
          </label>
          <input
            id="email"
            type="email"
            autoComplete="username"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
          />
        </div>

        <div className="space-y-2">
          <label htmlFor="password" className="block text-sm font-medium text-slate-700">
            Password
          </label>
          <input
            id="password"
            type="password"
            autoComplete="current-password"
            required
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="w-full rounded-lg border border-slate-300 px-3 py-2 text-sm focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-500/30"
          />
        </div>

        {error && (
          <div
            role="alert"
            className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 border border-red-200"
          >
            {error}
          </div>
        )}

        <button
          type="submit"
          disabled={submitting || !email || !password}
          className="w-full rounded-lg bg-brand-700 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-brand-700/90 disabled:cursor-not-allowed disabled:bg-slate-300"
        >
          {submitting ? 'Verificando…' : 'Iniciar sesión'}
        </button>
      </form>
    </main>
  );
}
